/**
 * Retail Model Configuration
 * COMPLETE extraction from /server/retail/retail-api.js
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const retailConfig: ModelConfig = {
  modelType: 'retail',
  apiEndpoint: '/api/retail',
  displayName: 'Retail',
  resultTabs: standardResultTabs,
  description: 'Physical retail store model with foot traffic, conversion, and inventory.',
  icon: '🏬',
  color: '#ec4899',

  assumptionGroups: [
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        { id: 'userEmail', label: 'Your Email', type: 'email', placeholder: 'name@company.com' },
      ],
    },
    {
      id: 'store-operations',
      title: '🏬 Core Store Operations',
      fields: [
        { id: 'storeSize', label: 'Store Size (sq ft)', type: 'number', defaultValue: 2500 },
        { id: 'rentPerSqFt', label: 'Monthly Rent/sq ft ($)', type: 'number', defaultValue: 35 },
        { id: 'avgTransactionValue', label: 'Avg Transaction ($)', type: 'number', defaultValue: 65 },
        { id: 'transactionsPerDay', label: 'Transactions/Day', type: 'number', defaultValue: 60 },
        { id: 'operatingDaysMonth', label: 'Operating Days/Month', type: 'number', defaultValue: 30 },
      ],
    },
    {
      id: 'growth',
      title: '📈 Growth by Year',
      fields: [
        { id: 'transactionGrowthY2', label: 'Y2 Transaction Growth (%)', type: 'number', defaultValue: 12 },
        { id: 'transactionGrowthY3', label: 'Y3 Transaction Growth (%)', type: 'number', defaultValue: 10 },
        { id: 'transactionGrowthY4', label: 'Y4 Transaction Growth (%)', type: 'number', defaultValue: 8 },
        { id: 'transactionGrowthY5', label: 'Y5 Transaction Growth (%)', type: 'number', defaultValue: 6 },
        { id: 'avgTransactionGrowth', label: 'Avg Transaction Growth (%)', type: 'number', defaultValue: 3 },
      ],
    },
    {
      id: 'product-mix',
      title: '📦 Product Mix & Pricing',
      fields: [
        { id: 'productCategoriesCount', label: 'Product Categories', type: 'number', defaultValue: 5 },
        { id: 'highMarginProductPct', label: 'High Margin Products (%)', type: 'number', defaultValue: 30 },
      ],
    },
    {
      id: 'inventory',
      title: '📊 Inventory Management',
      fields: [
        { id: 'inventoryTurnover', label: 'Inventory Turnover', type: 'number', defaultValue: 6 },
        { id: 'inventoryInvestment', label: 'Initial Inventory ($)', type: 'number', defaultValue: 180000 },
        { id: 'inventoryMarginBuffer', label: 'Safety Stock (%)', type: 'number', defaultValue: 15 },
      ],
    },
    {
      id: 'cost-structure',
      title: '💵 Cost Structure',
      fields: [
        { id: 'productCostPct', label: 'Product Cost (%)', type: 'number', defaultValue: 55 },
        { id: 'laborCostPct', label: 'Labor Cost (%)', type: 'number', defaultValue: 20 },
        { id: 'utilitiesPct', label: 'Utilities (%)', type: 'number', defaultValue: 4 },
      ],
    },
    {
      id: 'operating-expenses',
      title: '🔧 Operating Expenses',
      fields: [
        { id: 'salesMarketingPct', label: 'Marketing (%)', type: 'number', defaultValue: 5 },
        { id: 'maintenancePct', label: 'Maintenance (%)', type: 'number', defaultValue: 2 },
        { id: 'suppliesPct', label: 'Supplies (%)', type: 'number', defaultValue: 1.5 },
        { id: 'shrinkagePct', label: 'Shrinkage (%)', type: 'number', defaultValue: 2 },
        { id: 'gaPct', label: 'G&A (%)', type: 'number', defaultValue: 8 },
        { id: 'monthlyTechCosts', label: 'Monthly Tech Costs ($)', type: 'number', defaultValue: 800 },
      ],
    },
    {
      id: 'expansion',
      title: '🚀 Expansion Planning',
      fields: [
        { id: 'sameStoreSalesGrowth', label: 'Same-Store Sales Growth (%)', type: 'number', defaultValue: 12 },
        { id: 'newStoreExpansionYear', label: 'Second Store Year', type: 'number', defaultValue: 3 },
        { id: 'secondStoreInvestment', label: 'Second Store Investment ($)', type: 'number', defaultValue: 150000 },
      ],
    },
    {
      id: 'store-metrics',
      title: '📊 Store-Specific Metrics',
      fields: [
        { id: 'footTrafficPerDay', label: 'Daily Foot Traffic', type: 'number', defaultValue: 150 },
        { id: 'conversionRate', label: 'Conversion Rate (%)', type: 'number', defaultValue: 40 },
        { id: 'avgItemsPerTransaction', label: 'Items/Transaction', type: 'number', defaultValue: 2.3, step: 0.1 },
        { id: 'avgItemPrice', label: 'Avg Item Price ($)', type: 'number', defaultValue: 28 },
      ],
    },
    {
      id: 'customer-analytics',
      title: '👥 Customer Analytics',
      fields: [
        { id: 'repeatCustomerRate', label: 'Repeat Customer Rate (%)', type: 'number', defaultValue: 45 },
        { id: 'customerLifetimeMonths', label: 'Customer Lifetime (mo)', type: 'number', defaultValue: 24 },
        { id: 'avgCustomerAcquisitionCost', label: 'Customer Acquisition Cost ($)', type: 'number', defaultValue: 15 },
      ],
    },
    {
      id: 'fixed-assets',
      title: '🏗️ Fixed Assets',
      fields: [
        { id: 'initialEquipmentValue', label: 'Equipment Value ($)', type: 'number', defaultValue: 100000 },
        { id: 'initialFurnitureValue', label: 'Furniture Value ($)', type: 'number', defaultValue: 50000 },
        { id: 'initialDecorValue', label: 'Decor Value ($)', type: 'number', defaultValue: 30000 },
        { id: 'equipmentDepreciationRate', label: 'Equipment Depreciation (%)', type: 'number', defaultValue: 20 },
        { id: 'furnitureDepreciationRate', label: 'Furniture Depreciation (%)', type: 'number', defaultValue: 15 },
        { id: 'initialLaunchCosts', label: 'Launch Costs ($)', type: 'number', defaultValue: 40000 },
      ],
    },
    {
      id: 'debt-interest',
      title: '🏦 Debt & Interest',
      fields: [
        { id: 'initialDebtAmount', label: 'Initial Debt ($)', type: 'number', defaultValue: 200000 },
        { id: 'annualInterestRate', label: 'Interest Rate (%)', type: 'number', defaultValue: 7 },
        { id: 'debtRepaymentYears', label: 'Repayment Years', type: 'number', defaultValue: 7 },
      ],
    },
    {
      id: 'working-capital',
      title: '💰 Working Capital',
      fields: [
        { id: 'daysInAccountsReceivable', label: 'Days in AR', type: 'number', defaultValue: 5 },
        { id: 'daysInAccountsPayable', label: 'Days in AP', type: 'number', defaultValue: 45 },
        { id: 'inventoryDays', label: 'Inventory Days', type: 'number', defaultValue: 60 },
        { id: 'prepaidExpensesPct', label: 'Prepaid Expenses (%)', type: 'number', defaultValue: 3 },
        { id: 'accruedExpensesPct', label: 'Accrued Expenses (%)', type: 'number', defaultValue: 6 },
        { id: 'securityDepositMonths', label: 'Security Deposit (mo)', type: 'number', defaultValue: 3 },
        { id: 'minimumCashBalance', label: 'Minimum Cash ($)', type: 'number', defaultValue: 30000 },
      ],
    },
    {
      id: 'capital-raising',
      title: '🚀 Capital Raising',
      fields: [
        { id: 'initialCapitalRaise', label: 'Initial Capital ($)', type: 'number', defaultValue: 300000 },
        { id: 'year2CapitalRaise', label: 'Year 2 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year3CapitalRaise', label: 'Year 3 Capital ($)', type: 'number', defaultValue: 150000 },
        { id: 'year4CapitalRaise', label: 'Year 4 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year5CapitalRaise', label: 'Year 5 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'automaticCapitalRaise', label: 'Automatic Capital Raise', type: 'checkbox', defaultValue: true, helpText: 'Automatically raise capital when cash goes negative' },
      ],
    },
    {
      id: 'other-financial',
      title: '📊 Other Financial',
      fields: [
        { id: 'maintenanceCapexPct', label: 'Maintenance CapEx (%)', type: 'number', defaultValue: 2 },
        { id: 'annualInsurance', label: 'Annual Insurance ($)', type: 'number', defaultValue: 18000 },
        { id: 'annualLicenses', label: 'Annual Licenses ($)', type: 'number', defaultValue: 3000 },
      ],
    },
    {
      id: 'financial-tax',
      title: '🏛️ Financial & Tax',
      fields: [
        { id: 'corporateTaxRate', label: 'Corporate Tax Rate (%)', type: 'number', defaultValue: 25 },
      ],
    },
  ],

};

export const retailDefaultAssumptions = {
  storeSize: 2500,
  rentPerSqFt: 35,
  avgTransactionValue: 65,
  transactionsPerDay: 60,
  operatingDaysMonth: 30,
  transactionGrowthY2: 12,
  transactionGrowthY3: 10,
  transactionGrowthY4: 8,
  transactionGrowthY5: 6,
  avgTransactionGrowth: 3,
  productCategoriesCount: 5,
  highMarginProductPct: 30,
  inventoryTurnover: 6,
  inventoryInvestment: 180000,
  inventoryMarginBuffer: 15,
  productCostPct: 55,
  laborCostPct: 20,
  utilitiesPct: 4,
  salesMarketingPct: 5,
  maintenancePct: 2,
  suppliesPct: 1.5,
  shrinkagePct: 2,
  gaPct: 8,
  monthlyTechCosts: 800,
  sameStoreSalesGrowth: 12,
  newStoreExpansionYear: 3,
  secondStoreInvestment: 150000,
  footTrafficPerDay: 150,
  conversionRate: 40,
  avgItemsPerTransaction: 2.3,
  avgItemPrice: 28,
  repeatCustomerRate: 45,
  customerLifetimeMonths: 24,
  avgCustomerAcquisitionCost: 15,
  initialLaunchCosts: 40000,
  corporateTaxRate: 25,
  initialEquipmentValue: 100000,
  initialFurnitureValue: 50000,
  initialDecorValue: 30000,
  equipmentDepreciationRate: 20,
  furnitureDepreciationRate: 15,
  initialDebtAmount: 200000,
  annualInterestRate: 7,
  debtRepaymentYears: 7,
  daysInAccountsReceivable: 5,
  daysInAccountsPayable: 45,
  inventoryDays: 60,
  prepaidExpensesPct: 3,
  accruedExpensesPct: 6,
  securityDepositMonths: 3,
  minimumCashBalance: 30000,
  initialCapitalRaise: 300000,
  year2CapitalRaise: 0,
  year3CapitalRaise: 150000,
  year4CapitalRaise: 0,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
  maintenanceCapexPct: 2,
  annualInsurance: 18000,
  annualLicenses: 3000,
  revenueMultiple: 0.8,
  ebitdaMultiple: 4,
  discountRate: 0.15,
  terminalGrowth: 0.02,
};
